import {Template} from "../../../components/template/template.jsx";
import Button from "../../../components/button/button.jsx";
import {FaFileLines} from "react-icons/fa6";
import {Card} from "../../../components/card/card.jsx";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useGeneralInput, useGeneralInputSpecialCase} from "../../../hooks/use-general-input.js";
import InputForm from "../../../components/input/input-form.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import InputDateTime from "../../../components/input/input-datetime.jsx";
import {FaEraser, FaMapMarkedAlt, FaSearch, FaTimes} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {useMutation, useQuery} from "@tanstack/react-query";
import sentryCloudSearchService from "../../../services/web/sentry-cloud-search-service.js";
import {MaskDefault} from "../../../utils/mask.js";
import {addMonths, format, isAfter, isBefore, subMonths} from "date-fns";
import {v4 as uuidv4} from "uuid";
import {getAssetPath} from "../../../utils/get-asset-path.js";
import projectUtils from "../../../utils/project-utils.js";
import {Modal} from "../../../components/modal/modal.jsx";
import SentryCloudImageModal from "./components/sentry-cloud-image-modal.jsx";
import CachedImageButton from "./components/cached-image-button.jsx";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useSearchParams} from "react-router";

const initialFilter = () => {
    const refDate = new Date();
    return {
        plate: "",
        dtStart: subMonths(refDate, 1),
        dtEnd: refDate,
        reason: ""
    }
}

const SentryCloudMapHeader = ({onOpenMap}) => (
    <div className="h-full flex mx-auto justify-center items-center">
        <Button title="Abrir no Mapa" target="primary" onClick={onOpenMap}>
            <FaMapMarkedAlt className={'transform -translate-y-px'}/> Ver todos no mapa
        </Button>
    </div>
);

const SentryCloudSearch = () => {
    const [searchParams] = useSearchParams();

    const [isLoading, setIsLoading] = useState(false);
    const [searchId, setSearchId] = useState(null);
    const [filter, setFilter] = useState(initialFilter());
    const [image, setImage] = useState(null);
    const handleInput = useGeneralInput(setFilter);
    const handleSpecialInput = useGeneralInputSpecialCase(setFilter);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const navigate = navigateHook();

    const openMap = useCallback(() => {
        const filters = {
            plate: filter.plate,
            dtStart: filter.dtStart ? format(filter.dtStart, "dd/MM/yyyy HH:mm") : null,
            dtEnd: filter.dtEnd ? format(filter.dtEnd, "dd/MM/yyyy HH:mm") : null,
            reason: filter.reason,
        };

        const encodedFilters = btoa(encodeURIComponent(JSON.stringify(filters)).replace(/%([0-9A-F]{2})/g, (_, code) => String.fromCharCode(parseInt(code, 16))));
        navigate(`/reports/sentry_cloud_search_map?d=${encodeURIComponent(encodedFilters)}`, true);
    }, [filter, navigate]);

    const openGoogleMaps = useCallback((latitude, longitude) => {
        if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) return;
        window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, "_blank");
    }, []);

    useEffect(() => {
        const plate = searchParams.get("plate");
        if (plate) setFilter(prev => ({...prev, plate}));
    }, [searchParams]);

    const {data: cities = [], isLoading: isLoadingCities} = useQuery({
        queryFn: sentryCloudSearchService.citiesSearch,
        queryKey: ['cities']
    });

    const { mutate: createSearch, isPending: isLoadingCreateSearch } = useMutation({
        mutationFn: (payload) =>
            sentryCloudSearchService.interligationCreate(payload),
        onSuccess: (data) => {
            setSearchId(data)
        }
    })

    const {data: searchResult = {}, isLoading: isLoadingSearchResult, refetch: refetchSearchResult} = useQuery({
        queryFn: () => sentryCloudSearchService.interligationResult(searchId),
        queryKey: ["resultKey", searchId],
        enabled: !!searchId
    });

    const { data: provisoryReport = {}, isLoading: isLoadingProvisoryReport } = useQuery({
        queryFn: sentryCloudSearchService.lastSearch,
        queryKey: ["lastSearch"]
    })

    const cityTableCols = useMemo(() => {
        const defaultColumns = [
            {headerName: "Município", field: "name", flex: 1,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }}
        ];

        if (!searchResult.cities) return defaultColumns

        return [...defaultColumns,
            {headerName: "Status", field: "status", flex: 1,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                },
                cellRenderer: ({value}) => <span className={getStatusColor(value) !== '' ? 'font-semibold' : 'opacity-70 italic'} style={{color: getStatusColor(value)}}>{value}</span>
            }]
    }, [searchResult]);

    const searchResultTableCols = useMemo(() => [
        {headerName: "Data/hora", field: "date", flex: 3,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Município", field: "city", flex: 3,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Sentido", field: "img", flex: 1.5,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            },
            cellRenderer: ({value}) =>
                <div className="h-full flex justify-center items-center">
                    <img src={getAssetPath(value.replace("/web/public/assets/", ""))}/>
                </div>
        },
        {headerName: "Local", colId: uuidv4(), flex: 8,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            },
            tooltipValueGetter: params => {
                const { cpName, rtDescr, laneObs } = params.data;
                if (cpName && rtDescr) return `${cpName} - ${rtDescr}${laneObs ? ' - ' + laneObs : ''}`;

                return cpName ?? "";
            },
            valueGetter: params => {
                const { cpName, rtDescr, laneObs } = params.data;
                if (cpName && rtDescr) return `${cpName} - ${rtDescr} ${laneObs ? ' - ' + laneObs : ''}`;

                return cpName ?? ""
            }
        },
        {headerName: "Imagem", field: "link", flex: 3, sortable: false,
            cellRenderer: CachedImageButton,
            cellRendererParams: {
                handleOpen: handleOpen
            }
        },
        {headerName: "", field: "", flex: 3, sortable: false,
            headerComponent: searchResult?.result?.length > 0 ? SentryCloudMapHeader : undefined,
            headerComponentParams: searchResult?.result?.length > 0 ? { onOpenMap: openMap } : undefined,
            cellRenderer: (params) => {
                const latitude = params.data?.lat ?? params.data?.latitude;
                const longitude = params.data?.lng ?? params.data?.longitude;

                return(
                    <div className="h-full flex justify-center items-center">
                        <Button title="Abrir no Google Maps" target="outline-primary" disabled={!latitude || !longitude} onClick={() => {openGoogleMaps(latitude, longitude);}}>
                            <img className={`${(!latitude || !longitude) ? 'opacity-60' : ''}`} src={getAssetPath("img/custom_icons/gis-icon.svg")}/>
                        </Button>
                    </div>
                )
            }
        },
    ], [openMap, openGoogleMaps, searchResult?.result?.length]);

    const provisoryReportTableCols = useMemo(() => [
        {headerName: "Data/hora", field: "date_time", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Município", field: "citysearch", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Login", field: "loginuser", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Placa", field: "plate", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Data inicial (Filtro)", field: "dateini", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Data final (Filtro)", field: "dateend", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Motivo (Filtro)", field: "reason", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
    ], []);

    const search = async () => {
        const isNotValid = validate(filter);

        if (isNotValid){
            projectUtils.swalErrorMessage("", isNotValid, "Ok");
            return;
        }

        createSearch(payloadFormatter(filter))
    }
    const clear = () => {
        setFilter(initialFilter())
        setSearchId(null);
    }

    function payloadFormatter(toFormat) {
        const formData = new FormData();
        formData.append("plate", toFormat.plate);
        formData.append("dtStart", format(toFormat.dtStart, "dd/MM/yyyy HH:mm"))
        formData.append("dtEnd", format(toFormat.dtEnd, "dd/MM/yyyy HH:mm"))
        formData.append("reason", toFormat.reason)
        return formData
    }

    function getStatusColor(status) {
        if (status === "Com passagem") return "#008a0e"
        if (status === "Offline") return "#ff0000"
        if (status === "Sem resposta") return "#ff8408"
        return ""
    }

    function validate(filter) {
        if (filter.plate === "") return `O campo "Placa" é obrigatório.`
        if (filter.dtStart === null) return `O campo "Data inicial" é obrigatório.`
        if (filter.dtEnd === null) return `O campo "Data final" é obrigatório.`
        if (isBefore(filter.dtEnd, filter.dtStart)) return `A data inicial não pode ser maior que a data final.`
        if (filter.reason === "") return `O campo "Motivo" é obrigatório.`

        const limit = addMonths(filter.dtStart, 1);
        if (isAfter(filter.dtEnd, limit)) return 'O período máximo de pesquisa é de 1 mês.'
        return ""
    }

    async function handleOpen(url){
        setShowImageModal(true);
        setImage(url);
    }

    const openInfoReport = () => {
        if (!searchResult.cities || !searchResult.result) return;
        sessionStorage.setItem("sentry-cloud-search-info", JSON.stringify(searchResult));
        sessionStorage.setItem("sentry-cloud-search-info-filters", JSON.stringify(filter));
        navigate('/reports/sentry-cloud-search-info-report', true);
    }

    const openAudit = () => {
        navigate('/reports/interconnected_search_audit', true);
    }

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clear);

    const customTitleForModal =
        <div className="flex w-full justify-between items-center">
            <h3 className="font-semibold text-gray-500" title={''}>Últimas pesquisas realizadas</h3>
            <div className="flex gap-4 items-center absolute right-5">
                <Button onClick={openAudit} title={'Abrir relatório'}><FaFileLines/>Auditoria</Button>
                <FaTimes onClick={() => setShowReportModal(false)} className="cursor-pointer content-center" title={'Fechar'}/>
            </div>
        </div>

    return (
        <Template isLoading={isLoading || isLoadingCreateSearch || isLoadingCities || isLoadingSearchResult || isLoadingProvisoryReport}>
            <Template.Header>
                <Template.Title>
                    Pesquisa de passagens - SentryCloud
                </Template.Title>
                <div className="flex gap-2">
                    <Button target="warning" onClick={() => setShowReportModal(true)}>Últimas pesquisas realizadas</Button>
                    { searchResult?.result &&
                        <Button onClick={openInfoReport}><FaFileLines/>Relatório</Button>
                    }
                </div>
            </Template.Header>
            <Template.Body className="space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className="col-span-2">
                                <InputForm
                                    id="plate"
                                    label="Placa *"
                                    value={filter.plate}
                                    onChange={({target}) => setFilter(prev => ({...prev, plate: target?.value.toUpperCase()}))}
                                    mask={MaskDefault.plate}
                                />
                            </div>
                            <div className="col-span-3">
                                <InputDateTime
                                    id="dtStart"
                                    label="Data inicial *"
                                    selected={filter.dtStart}
                                    onChange={date => handleSpecialInput("dtStart", date)}
                                />
                            </div>
                            <div className="col-span-3">
                                <InputDateTime
                                    id="dtEnd"
                                    label="Data final *"
                                    selected={filter.dtEnd}
                                    onChange={date => handleSpecialInput("dtEnd", date)}
                                />
                            </div>
                            <div className="col-span-4 flex gap-4">
                                <div className="flex-1">
                                    <InputForm
                                        id="reason"
                                        label="Motivo *"
                                        value={filter.reason}
                                        onChange={handleInput}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button title="Limpar" target="outline-primary" onClick={clear}><FaEraser/></Button>
                                    <Button title="Buscar" onClick={search}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <div className="flex flex-col gap-2 h-[calc(100vh-131px)] overflow-y-auto">
                    <Card>
                        <Card.Body>
                            { cities.length > 0
                                ?
                                <div style={{height: "260px"}}>
                                    <AgGridReact
                                        rowData={searchResult.cities ? searchResult.cities : cities}
                                        columnDefs={cityTableCols}
                                        suppressHorizontalScroll={true}
                                        suppressCellFocus={true}
                                        suppressHeaderFocus={true}
                                        animateRows={false}
                                        theme={aggridUtils.generalTheme()}
                                        gridOptions={{localeText: aggridUtils.localization()}}
                                        defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                    />
                                </div>
                                : <div className="bg-[#f9e1e5] font-bold text-[#af233a] p-2 text-[16px] rounded">SentryCloud offline.</div>
                            }
                        </Card.Body>
                    </Card>
                    { searchResult?.result &&
                        <Card>
                            <Card.Body className="flex-1">
                                <AgGridReact
                                    tooltipShowDelay={500}
                                    suppressHorizontalScroll={true}
                                    suppressCellFocus={true}
                                    domLayout="autoHeight"
                                    animateRows={false}
                                    suppressHeaderFocus={true}
                                    rowData={searchResult?.result ?? []}
                                    columnDefs={searchResultTableCols}
                                    theme={aggridUtils.generalTheme({rowHeight: 40})}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                />
                            </Card.Body>
                        </Card>
                    }
                </div>
                { provisoryReport &&
                    <Modal open={showReportModal} setOpen={setShowReportModal} title={customTitleForModal} showCloseButton={false}>
                        <Modal.Body>
                            <div className="h-[600px] flex flex-col">
                                <div className="flex w-full justify-center mb-2 text-[15px]">
                                    <span>Exibindo as últimas <span className="font-bold">{provisoryReport?.values?.length}</span> de <span className="font-bold">{provisoryReport?.size}</span>  pesquisas realizadas</span>
                                </div>
                                <AgGridReact
                                    suppressHorizontalScroll={true}
                                    suppressCellFocus={true}
                                    suppressHeaderFocus={true}
                                    animateRows={false}
                                    rowData={provisoryReport.values}
                                    columnDefs={provisoryReportTableCols}
                                    theme={aggridUtils.generalTheme()}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                />
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="flex gap-2 items-center">
                                <span>O relatório completo está disponível na auditoria.</span>
                            </div>
                        </Modal.Footer>
                    </Modal>
                }
                <SentryCloudImageModal showModal={showImageModal} setShowModal={setShowImageModal} imgUrl={image}/>
            </Template.Body>
        </Template>
    )
}

export default SentryCloudSearch;